import { useState } from 'react';
import logoRX from '../../../assets/ReserveX-Logo-1k.png';
import loginBg from '../../../assets/goldbg.png';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';
import Card from '../../../components/base/Card';

interface LoginFormProps {
  onLogin: (credentials: { email: string; password: string }) => void;
}

export default function LoginForm({ onLogin }: LoginFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:9080';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        let reason = `Login failed (${res.status})`;
        try {
          const data = await res.json();
          if (data?.reason) reason = data.reason;
        } catch {}
        throw new Error(reason);
      }

      const data = await res.json();
      const token = data?.token as string | undefined;
      if (!token) throw new Error('Missing token in response');

      // Persist JWT for subsequent requests
      localStorage.setItem('token', token);

      // Let parent know we’re authenticated (keeps existing parent toggle flow)
      onLogin({ email, password });
    } catch (err: any) {
      setError(err?.message || 'Authentication error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center relative"
      style={{
        backgroundImage: `url(${loginBg})`,
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-black/65 via-neutral-950/55 to-black/70"></div>

      <div className="relative z-10 w-full max-w-md px-6">
        <Card className="p-8 backdrop-blur-sm bg-black/75 border-accent-700/50">
          <div className="text-center mb-8">
            <img src={logoRX} alt="ReserveX logo" className="w-32 h-32 rounded-xl object-cover mx-auto mb-4" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-accent-300 to-accentAlt-400 bg-clip-text text-transparent mb-2">
              ReserveX Capital Ltd
            </h1>
            <p className="text-zinc-300">Virtual Data Room Access</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              type="email"
              label="Email Address"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              icon="ri-mail-line"
              required
              disabled={isLoading}
            />

            <Input
              type="password"
              label="Password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              icon="ri-lock-line"
              required
              disabled={isLoading}
            />

            {error && (
              <div className="text-red-600 text-sm" role="alert" aria-live="assertive">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Authenticating...
                </>
              ) : (
                <>
                  <i className="ri-login-box-line mr-2"></i>
                  Secure Login
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-zinc-400">
              Authorized access only. All activities are monitored and logged.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
